import { useEffect, useId, useState } from 'react';
import { AppBar, Toolbar, Container, Box, Button, Collapse, IconButton, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface NavLink {
  href: string;
  label: string;
}

/** Rutas absolutas (`/#…`) para que lleven a las secciones del home desde cualquier página. */
const defaultLinks: NavLink[] = [
  { href: '/#solution', label: 'Cómo funciona' },
  { href: '/#features', label: 'Características' },
  { href: '/#integrations', label: 'Recursos' },
  { href: '/#pricing', label: 'Precios' },
];

/** Landings verticales del submenú "Soluciones". Añadir aquí las nuevas. */
const solutions: NavLink[] = [
  { href: '/turismo', label: 'Turismo' },
];

export interface NavbarProps {
  onOpenContact: () => void;
  /**
   * Enlaces del menú. Por defecto, las anclas de la landing principal precedidas
   * del submenú "Soluciones". Con enlaces personalizados no se muestra
   * "Soluciones": el submenú forma parte solo del menú por defecto.
   */
  links?: NavLink[];
  /** Etiqueta junto al logo, tras un separador (p. ej. "Turismo & Hostelería"). */
  tagline?: string;
  /**
   * Botón único que sustituye a "Ingresar" + "Agenda un demo". Enlaza a `href`
   * en lugar de abrir el modal de contacto.
   */
  cta?: NavLink;
}

const linkSx = {
  color: 'text.secondary', textDecoration: 'none', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
  '&:hover': { color: 'primary.main' },
} as const;

export default function Navbar({ onOpenContact, links: customLinks, tagline, cta }: NavbarProps) {
  const links = customLinks ?? defaultLinks;
  const showSolutions = customLinks === undefined;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [solutionsAnchor, setSolutionsAnchor] = useState<HTMLElement | null>(null);
  const [solutionsMobileOpen, setSolutionsMobileOpen] = useState(false);
  const solutionsId = useId();
  const solutionsMenuId = `${solutionsId}-menu`;
  const solutionsButtonId = `${solutionsId}-button`;
  const solutionsMobileId = `${solutionsId}-mobile`;
  const theme = useTheme();
  // Por debajo de `lg` (1200 px) la fila logo + enlaces + botones no cabe sin
  // solaparse (el menú por defecto necesita ~1010 px): se pasa a hamburguesa.
  const mobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) setSolutionsMobileOpen(false);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          background: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(14px)',
          color: 'text.primary',
          borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
          transition: 'all 0.2s',
        }}
      >
        <Container disableGutters>
          <Toolbar sx={{ minHeight: 64, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box component="a" href="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
              <Box component="img" src="/logo-maia.svg" alt="MaIA" sx={{ height: 32 }} />
              {tagline && (
                <Box component="span" sx={{
                  ml: 1.25, pl: 1.25, borderLeft: '1px solid var(--border2)',
                  fontSize: 12, fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap',
                }}>
                  {tagline}
                </Box>
              )}
            </Box>

            {!mobile && (
              <Box component="ul" sx={{ display: 'flex', alignItems: 'center', gap: 3, listStyle: 'none', m: 0, p: 0 }}>
                {links.map(l => (
                  <li key={l.href}>
                    <Box component="a" href={l.href} sx={linkSx}>{l.label}</Box>
                  </li>
                ))}
                {showSolutions && (
                  <li>
                    <Box
                      component="button"
                      type="button"
                      id={solutionsButtonId}
                      aria-haspopup="true"
                      aria-expanded={solutionsAnchor ? 'true' : 'false'}
                      aria-controls={solutionsAnchor ? solutionsMenuId : undefined}
                      onClick={e => setSolutionsAnchor(e.currentTarget)}
                      sx={{
                        ...linkSx, display: 'flex', alignItems: 'center', gap: 0.25,
                        background: 'none', border: 0, p: 0, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Soluciones
                      <ExpandMoreIcon fontSize="small" />
                    </Box>
                    <Menu
                      id={solutionsMenuId}
                      anchorEl={solutionsAnchor}
                      open={Boolean(solutionsAnchor)}
                      onClose={() => setSolutionsAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      MenuListProps={{ 'aria-labelledby': solutionsButtonId }}
                    >
                      {solutions.map(s => (
                        <MenuItem key={s.href} component="a" href={s.href} onClick={() => setSolutionsAnchor(null)}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </li>
                )}
              </Box>
            )}

            {!mobile ? (
              cta ? (
                <Button href={cta.href} variant="contained" size="small" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{cta.label}</Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <Button href="https://app.maiabuilder.ai/" variant="contained" size="small">Ingresar</Button>
                  <Button onClick={onOpenContact} variant="outlined" size="small">Agenda un demo</Button>
                </Box>
              )
            ) : (
              <IconButton onClick={() => setOpen(true)} aria-label="Menú">
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {mobile && open && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 1300,
          bgcolor: 'background.paper', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 3,
        }}>
          <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 16, right: 16 }} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
          {showSolutions && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                component="button"
                type="button"
                aria-expanded={solutionsMobileOpen ? 'true' : 'false'}
                aria-controls={solutionsMobileId}
                onClick={() => setSolutionsMobileOpen(v => !v)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  fontSize: 20, color: 'text.primary', fontWeight: 600, fontFamily: 'inherit',
                  background: 'none', border: 0, p: 0, cursor: 'pointer',
                }}
              >
                Soluciones
                <ExpandMoreIcon sx={{
                  transition: 'transform 0.2s',
                  transform: solutionsMobileOpen ? 'rotate(180deg)' : 'none',
                }} />
              </Box>
              <Collapse in={solutionsMobileOpen} id={solutionsMobileId} unmountOnExit>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                  {solutions.map(s => (
                    <Box key={s.href} component="a" href={s.href} onClick={() => setOpen(false)}
                      sx={{ fontSize: 17, color: 'text.secondary', textDecoration: 'none', fontWeight: 500 }}>
                      {s.label}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
          {links.map(l => (
            <Box key={l.href} component="a" href={l.href} onClick={() => setOpen(false)}
              sx={{ fontSize: 20, color: 'text.primary', textDecoration: 'none', fontWeight: 600 }}>
              {l.label}
            </Box>
          ))}
          {cta ? (
            <Button
              variant="contained"
              size="large"
              href={cta.href}
              onClick={() => setOpen(false)}
              sx={{ mt: 2 }}
            >
              {cta.label}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                href="https://app.maiabuilder.ai/"
                onClick={() => setOpen(false)}
              >
                Ingresar
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => { setOpen(false); onOpenContact(); }}
              >
                Agenda un demo →
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
